import * as monthUtils from '@actual-app/core/shared/months';

import type {
  PlanningBudgetCategorySummary,
  PlanningBudgetMonth,
  PlanningGoal,
  PlanningGoalAllocation,
  PlanningGoalKind,
  PlanningGoalProjectionPoint,
  PlanningGoalRecommendation,
} from './types';

const PLANNING_GOAL_KINDS: PlanningGoalKind[] = [
  'emergency-fund',
  'reserve',
  'sinking-fund',
  'car',
  'vacation',
  'home',
  'wedding',
  'loan-payoff',
  'custom',
];

type SerializedPlanningGoal = Partial<
  Omit<PlanningGoal, 'allocations'> & {
    allocations: Partial<PlanningGoalAllocation>[];
  }
>;

export function parsePlanningGoals(rawValue: string | undefined) {
  if (!rawValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return parsedValue
      .map((goal, index) => normalizePlanningGoal(goal, index))
      .filter((goal): goal is PlanningGoal => goal !== null);
  } catch {
    return [];
  }
}

export function serializePlanningGoals(goals: PlanningGoal[]) {
  return JSON.stringify(goals);
}

export function getPlanningGoalSavedAmount(
  goal: PlanningGoal,
  categorySummary?: PlanningBudgetCategorySummary,
  payoffAccountBalance?: number | null,
) {
  if (
    goal.kind === 'loan-payoff' &&
    goal.payoffAccountId &&
    payoffAccountBalance != null
  ) {
    const remainingPayoffBalance =
      getRemainingPayoffBalance(payoffAccountBalance) ?? 0;
    return Math.min(
      goal.targetAmount,
      Math.max(0, goal.targetAmount - remainingPayoffBalance),
    );
  }

  if (goal.categoryBalanceAtLastSync != null && categorySummary) {
    return Math.max(
      0,
      goal.savedAmountAtLastSync +
        categorySummary.balance -
        goal.categoryBalanceAtLastSync,
    );
  }

  return Math.max(0, goal.savedAmountAtLastSync);
}

export function getPlanningGoalLeftToSave(
  goal: PlanningGoal,
  categorySummary?: PlanningBudgetCategorySummary,
  payoffAccountBalance?: number | null,
) {
  return Math.max(
    0,
    goal.targetAmount -
      getPlanningGoalSavedAmount(goal, categorySummary, payoffAccountBalance),
  );
}

export function getPlanningGoalProgress(
  goal: PlanningGoal,
  categorySummary?: PlanningBudgetCategorySummary,
  payoffAccountBalance?: number | null,
) {
  if (goal.targetAmount <= 0) {
    return 0;
  }

  return Math.min(
    1,
    getPlanningGoalSavedAmount(goal, categorySummary, payoffAccountBalance) /
      goal.targetAmount,
  );
}

export function calculatePlanningGoalMonthlyContribution({
  goalKind,
  currentAmount,
  targetAmount,
  startMonth,
  targetMonth,
  annualPercentageRate,
}: {
  goalKind: PlanningGoalKind;
  currentAmount: number;
  targetAmount: number;
  startMonth: string;
  targetMonth: string | null;
  annualPercentageRate: number;
}) {
  if (!targetMonth) {
    return null;
  }

  const remainingAmount = Math.max(0, targetAmount - currentAmount);
  if (remainingAmount === 0) {
    return 0;
  }

  const monthCount = getPlanningGoalMonthCount(startMonth, targetMonth);
  const monthlyRate = Math.max(0, annualPercentageRate) / 100 / 12;
  const isLoanPayoffGoal = goalKind === 'loan-payoff';

  if (monthlyRate === 0) {
    return Math.max(0, Math.round(remainingAmount / monthCount));
  }

  if (isLoanPayoffGoal) {
    const paymentFactor = 1 - Math.pow(1 + monthlyRate, -monthCount);

    if (paymentFactor <= 0) {
      return Math.max(0, Math.round(remainingAmount / monthCount));
    }

    return Math.max(
      0,
      Math.round((remainingAmount * monthlyRate) / paymentFactor),
    );
  }

  const growthFactor = Math.pow(1 + monthlyRate, monthCount);
  const contributionFactor = (growthFactor - 1) / monthlyRate;
  const monthlyContribution =
    (targetAmount - currentAmount * growthFactor) / contributionFactor;

  return Math.max(0, Math.round(monthlyContribution));
}

export function buildPlanningGoalProjection({
  goalKind,
  currentAmount,
  targetAmount,
  startMonth,
  targetMonth,
  annualPercentageRate,
}: {
  goalKind: PlanningGoalKind;
  currentAmount: number;
  targetAmount: number;
  startMonth: string;
  targetMonth: string | null;
  annualPercentageRate: number;
}) {
  if (!targetMonth) {
    return [] as PlanningGoalProjectionPoint[];
  }

  const monthlyContribution = calculatePlanningGoalMonthlyContribution({
    goalKind,
    currentAmount,
    targetAmount,
    startMonth,
    targetMonth,
    annualPercentageRate,
  });

  if (monthlyContribution === null) {
    return [];
  }

  const monthDistance = Math.max(
    0,
    monthUtils.differenceInCalendarMonths(targetMonth, startMonth),
  );
  const midpointOffset = Math.floor(monthDistance / 2);
  const midpointMonth =
    monthDistance > 1 ? monthUtils.addMonths(startMonth, midpointOffset) : null;

  const pointMonths = [startMonth, midpointMonth, targetMonth].filter(
    (month, index, allMonths): month is string =>
      month != null && allMonths.indexOf(month) === index,
  );

  return pointMonths.map(month => {
    if (month === startMonth) {
      return { month, amount: currentAmount };
    }

    const contributionCount =
      monthUtils.differenceInCalendarMonths(month, startMonth) + 1;

    return {
      month,
      amount: projectAmount({
        goalKind,
        currentAmount,
        targetAmount,
        monthlyContribution,
        annualPercentageRate,
        contributionCount,
      }),
    };
  });
}

export function getPlanningBudgetCategorySummaries(
  budgetMonth: PlanningBudgetMonth | undefined,
) {
  if (!budgetMonth) {
    return [];
  }

  return budgetMonth.categoryGroups.flatMap(group => {
    if (!isBudgetCategoryGroup(group) || group.is_income) {
      return [];
    }

    return group.categories.map(category => ({
      id: category.id,
      groupId: group.id,
      name: category.name,
      groupName: group.name,
      budgeted: toFiniteNumber(category.budgeted),
      spent: toFiniteNumber(category.spent),
      balance: toFiniteNumber(category.balance),
    }));
  });
}

export function getPlanningBudgetCategoryMap(
  budgetMonth: PlanningBudgetMonth | undefined,
) {
  return new Map(
    getPlanningBudgetCategorySummaries(budgetMonth).map(summary => [
      summary.id,
      summary,
    ]),
  );
}

export function getPlanningGoalRecommendation({
  goal,
  currentMonth,
  categorySummary,
  payoffAccountBalance,
}: {
  goal: PlanningGoal;
  currentMonth: string;
  categorySummary?: PlanningBudgetCategorySummary;
  payoffAccountBalance?: number | null;
}): PlanningGoalRecommendation {
  const savedAmount = getPlanningGoalSavedAmount(
    goal,
    categorySummary,
    payoffAccountBalance,
  );
  if (savedAmount >= goal.targetAmount) {
    return {
      monthlyContribution: 0,
      status: 'funded',
      currentCategoryBudgeted: categorySummary?.budgeted ?? null,
    };
  }

  const monthlyContribution = calculatePlanningGoalMonthlyContribution({
    goalKind: goal.kind,
    currentAmount: savedAmount,
    targetAmount: goal.targetAmount,
    startMonth: currentMonth,
    targetMonth: goal.targetMonth,
    annualPercentageRate: goal.annualPercentageRate,
  });

  if (!goal.categoryId || !categorySummary) {
    return {
      monthlyContribution,
      status: 'missing-category',
      currentCategoryBudgeted: null,
    };
  }

  if (!goal.targetMonth || monthlyContribution === null) {
    return {
      monthlyContribution,
      status: 'flexible',
      currentCategoryBudgeted: categorySummary.budgeted,
    };
  }

  if (
    monthlyContribution === 0 ||
    categorySummary.budgeted >= monthlyContribution
  ) {
    return {
      monthlyContribution,
      status: 'on-track',
      currentCategoryBudgeted: categorySummary.budgeted,
    };
  }

  if (categorySummary.budgeted > 0) {
    return {
      monthlyContribution,
      status: 'below-target',
      currentCategoryBudgeted: categorySummary.budgeted,
    };
  }

  return {
    monthlyContribution,
    status: 'budget-this-month',
    currentCategoryBudgeted: categorySummary.budgeted,
  };
}

export function getPlanningGoalProjectedAmountAtTarget({
  goal,
  currentMonth,
  categorySummary,
  payoffAccountBalance,
  monthlyContribution,
}: {
  goal: PlanningGoal;
  currentMonth: string;
  categorySummary?: PlanningBudgetCategorySummary;
  payoffAccountBalance?: number | null;
  monthlyContribution?: number | null;
}) {
  if (!goal.targetMonth) {
    return null;
  }

  const projectedMonthlyContribution =
    monthlyContribution ?? categorySummary?.budgeted ?? null;

  if (projectedMonthlyContribution == null) {
    return null;
  }

  return projectAmount({
    goalKind: goal.kind,
    currentAmount: getPlanningGoalSavedAmount(
      goal,
      categorySummary,
      payoffAccountBalance,
    ),
    targetAmount: goal.targetAmount,
    monthlyContribution: Math.max(0, projectedMonthlyContribution),
    annualPercentageRate: goal.annualPercentageRate,
    contributionCount: getPlanningGoalMonthCount(
      currentMonth,
      goal.targetMonth,
    ),
  });
}

function normalizePlanningGoal(
  value: unknown,
  index: number,
): PlanningGoal | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const goal = value as SerializedPlanningGoal;
  const allocations = normalizePlanningAllocations(goal.allocations);
  const targetAmount = Math.max(0, toFiniteNumber(goal.targetAmount));
  const legacySavedAmount = allocations.reduce(
    (sum, allocation) => sum + allocation.amount,
    0,
  );

  return {
    id: getString(goal.id) ?? `planning-goal-${index}`,
    kind: isPlanningGoalKind(goal.kind) ? goal.kind : 'custom',
    name: getString(goal.name) ?? '',
    targetAmount,
    targetMonth: normalizePlanningMonth(goal.targetMonth),
    annualPercentageRate: Math.max(
      0,
      toFiniteNumber(goal.annualPercentageRate),
    ),
    categoryId: getString(goal.categoryId) ?? null,
    payoffAccountId: getString(goal.payoffAccountId) ?? null,
    savedAmountAtLastSync:
      toFiniteNumberOrNull(goal.savedAmountAtLastSync) ?? legacySavedAmount,
    categoryBalanceAtLastSync: toFiniteNumberOrNull(
      goal.categoryBalanceAtLastSync,
    ),
    allocations,
    createdAt: getString(goal.createdAt) ?? new Date(0).toISOString(),
    updatedAt: getString(goal.updatedAt) ?? new Date(0).toISOString(),
  };
}

function normalizePlanningAllocations(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((allocation, index) => normalizePlanningAllocation(allocation, index))
    .filter(
      (allocation): allocation is PlanningGoalAllocation => allocation !== null,
    );
}

function normalizePlanningAllocation(value: unknown, index: number) {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const allocation = value as Partial<PlanningGoalAllocation>;
  const accountId = getString(allocation.accountId);
  const amount = Math.max(0, toFiniteNumber(allocation.amount));

  if (!accountId) {
    return null;
  }

  return {
    id: getString(allocation.id) ?? `planning-allocation-${index}`,
    accountId,
    amount,
  };
}

function getPlanningGoalMonthCount(startMonth: string, targetMonth: string) {
  if (monthUtils.isBefore(targetMonth, startMonth)) {
    return 1;
  }

  return Math.max(1, monthUtils.rangeInclusive(startMonth, targetMonth).length);
}

function projectAmount({
  goalKind,
  currentAmount,
  targetAmount,
  monthlyContribution,
  annualPercentageRate,
  contributionCount,
}: {
  goalKind: PlanningGoalKind;
  currentAmount: number;
  targetAmount: number;
  monthlyContribution: number;
  annualPercentageRate: number;
  contributionCount: number;
}) {
  const monthlyRate = Math.max(0, annualPercentageRate) / 100 / 12;

  if (goalKind === 'loan-payoff') {
    let remainingAmount = Math.max(0, targetAmount - currentAmount);

    for (let index = 0; index < contributionCount; index += 1) {
      remainingAmount = Math.max(
        0,
        Math.round(remainingAmount * (1 + monthlyRate) - monthlyContribution),
      );
    }

    return Math.min(targetAmount, Math.max(0, targetAmount - remainingAmount));
  }

  let amount = currentAmount;

  for (let index = 0; index < contributionCount; index += 1) {
    amount = Math.round(amount * (1 + monthlyRate) + monthlyContribution);
  }

  return amount;
}

function normalizePlanningMonth(value: unknown) {
  if (typeof value !== 'string') {
    return null;
  }

  return /^\d{4}-\d{2}$/.test(value) ? value : null;
}

function isPlanningGoalKind(value: unknown): value is PlanningGoalKind {
  return (
    typeof value === 'string' &&
    PLANNING_GOAL_KINDS.includes(value as PlanningGoalKind)
  );
}

function getString(value: unknown) {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function toFiniteNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function toFiniteNumberOrNull(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function getRemainingPayoffBalance(balance: number | null | undefined) {
  if (balance == null) {
    return null;
  }

  return Math.max(0, -balance);
}

function isBudgetCategoryGroup(value: Record<string, unknown>): value is {
  id: string;
  name: string;
  is_income?: boolean;
  categories: Array<{
    id: string;
    name: string;
    budgeted?: number;
    spent?: number;
    balance?: number;
  }>;
} {
  return (
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    Array.isArray(value.categories)
  );
}
